/**
 * Anonymized swimlane timeline of recalibration events across the top N
 * most-adapted users in the trailing window. Each user gets a horizontal
 * lane, each event is a circle whose size scales with the count that day.
 */
import React, { useMemo } from 'react';
import { CEDAR } from '../theme';
import { formatInt } from '../format';

export interface SwimlaneEvent {
  day: string; // YYYY-MM-DD
  count: number;
}

export interface Swimlane {
  user_token: string;
  total: number;
  events: SwimlaneEvent[];
}

export interface RecalibrationTimelineProps {
  swimlanes: Swimlane[];
  days: number;
}

export const RecalibrationTimeline: React.FC<RecalibrationTimelineProps> = ({
  swimlanes,
  days,
}) => {
  const { daysArray, dayIndex } = useMemo(() => {
    const today = new Date();
    const arr: string[] = [];
    const idx: Record<string, number> = {};
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      idx[iso] = days - 1 - i;
      arr.push(iso);
    }
    return { daysArray: arr, dayIndex: idx };
  }, [days]);

  const maxCount = useMemo(() => {
    let m = 0;
    for (const l of swimlanes) for (const e of l.events) if (e.count > m) m = e.count;
    return m || 1;
  }, [swimlanes]);

  if (!swimlanes.length) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-gray-500">
        No recalibrations in the trailing window yet.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header strip showing day ticks */}
      <div className="flex items-center gap-3 pl-[132px]">
        <div className="relative flex-1">
          <div className="h-px bg-gray-100" />
          {daysArray.map((d, i) => {
            const date = new Date(d + 'T00:00:00');
            const showLabel = i === 0 || i === daysArray.length - 1 || date.getDate() === 1;
            return (
              <div
                key={d}
                className="absolute top-0 text-[9px] text-gray-400"
                style={{ left: `${(i / (daysArray.length - 1)) * 100}%`, transform: 'translateX(-50%)' }}
              >
                {showLabel && date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>
            );
          })}
        </div>
      </div>
      <div className="space-y-1.5">
        {swimlanes.map((lane) => (
          <div key={lane.user_token} className="group flex items-center gap-3">
            <div className="flex w-[120px] shrink-0 items-center justify-between gap-2 pr-3">
              <span className="truncate font-mono text-[11px] text-gray-700">{lane.user_token}</span>
              <span className="text-[10px] font-semibold text-gray-400 tabular-nums">
                {formatInt(lane.total)}
              </span>
            </div>
            <div className="relative h-6 flex-1 rounded-md bg-gray-50">
              <div className="absolute inset-x-2 top-1/2 h-px -translate-y-1/2 bg-gray-200" />
              {lane.events.map((ev) => {
                const idx = dayIndex[ev.day] ?? -1;
                if (idx < 0) return null;
                const x = (idx / (daysArray.length - 1)) * 100;
                const r = 4 + Math.min(6, (ev.count / maxCount) * 6);
                return (
                  <div
                    key={`${lane.user_token}-${ev.day}`}
                    className="absolute top-1/2"
                    style={{
                      left: `${x}%`,
                      transform: 'translate(-50%, -50%)',
                      width: r * 2,
                      height: r * 2,
                      borderRadius: '9999px',
                      background: CEDAR.stream,
                      boxShadow: `0 0 0 1px var(--surface), 0 1px 3px ${CEDAR.stream}55`,
                    }}
                    title={`${ev.day} · ${ev.count} recalibration${ev.count === 1 ? '' : 's'}`}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
